import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { Table, Typography, Card as CardAnt } from 'antd';

import {
  ColSize,
  ContentSection,
  Form,
  InputForm,
  RowSize,
  SelectForm,
} from '../../../../../../../../../../../../../shared/components';

import { GenericDataFormContext, QuotationInfoContext } from '../../../../../../../../../shared/contexts';
import { StepFourFormContext } from '../../../../contexts/StepFourForm';
import { StepTwoFormContext } from '../../../../../../../StepTwo/contexts/StepTwoForm/StepTwoFormContext';

import { ConsortiumQuotationComponent } from '../../../../../../../../../../../../../shared/interfaces';

import {
  formattedPrice,
  ValidateForm,
} from '../../../../../../../../../../../../../shared/utils';
import { COLORS } from '../../../../../../../../../../../../../config/themes/colors';

import './index.css';
import { TransferRightsForm } from '../../../TransferRightsForm';

interface Coverage {
  codigo: string;
  description: string;
  familyCode?: string;
  familyDescription?: string;
  key: string;
  riskCode: string;
  sumInsured: string;
  typeCoverage?: string;
}

interface CoverageGroup {
  [familyCode: string]: Coverage[];
}

interface FormCoveragesProps {
  consortiumComponent: ConsortiumQuotationComponent;
}

const MOCK_TABLE_COLUMNS_HEADER = [
  {
    title: 'Descripción de cobertura',
    dataIndex: 'description',
    width: '50%',
  },
  {
    title: 'Suma asegurada',
    dataIndex: 'sumInsured',
    width: '50%',
  },
];

export const FormCoverages: FC<FormCoveragesProps> = ({
  consortiumComponent,
}) => {
  const validateForm = new ValidateForm();

  const { propertyDataForm } = useContext(StepFourFormContext);
  const { provinceList, allLocalitiesPerProvince, allCoveragesPerPlan } = useContext(
    GenericDataFormContext
  );
  const { stepTwoForm, planOptionsBySelectedPlan } =
    useContext(StepTwoFormContext);

  const [coveragesListInfo, setCoveragesListInfo] = useState<CoverageGroup[]>(
    []
  );

  const provinceValueByTab = stepTwoForm?.getFieldValue(
    `province-tab-${consortiumComponent.nroComponente}`
  );
  const locationValueByTab = stepTwoForm?.getFieldValue(
    `location-tab-${consortiumComponent.nroComponente}`
  );
  const localitiesPerProvince = allLocalitiesPerProvince.find(
    (loc) => String(loc.provinceCode) === String(provinceValueByTab)
  )?.localitiesOptions;

  const setValuesForm = () => {
    propertyDataForm.setFieldValue(
      `propertyDataProvince${consortiumComponent.nroComponente}`,
      provinceValueByTab
    );
    propertyDataForm.setFieldValue(
      `propertyDataLocality${consortiumComponent.nroComponente}`,
      locationValueByTab
    );
  };

  const propertyDataAddressValueByTab = stepTwoForm?.getFieldValue(`propertyDataAddress${consortiumComponent.nroComponente}`);

  useEffect(() => {
    setValuesForm();
  }, [consortiumComponent]);

  const setCompleteCoveragesListInfo = useCallback(() => {

    const planSelected = stepTwoForm?.getFieldValue(`plan-tab-${consortiumComponent.nroComponente}`);

    const matchingCoveragesByTab = allCoveragesPerPlan.find(
      (el) => (el.plan as string) === String(planSelected)
    );

    if (matchingCoveragesByTab) {
      
      const filteredCoverages = matchingCoveragesByTab.coverages.filter(
        (planCoverage) =>
          consortiumComponent.cobertura.some(
            (cov) => cov.cobertura === planCoverage.codigo
          )
      );

      const coveragesListInfo = filteredCoverages.map((planCoverage) => {
        const matchedCoverage = consortiumComponent.cobertura.find(
          (cov) => cov.cobertura === planCoverage.codigo
        );

        let familyDescription;

        if (planCoverage?.codigoFamilia) {
          familyDescription = planCoverage.descripcionFamilia;
        } else if (planCoverage.tipoDeCobertura === 'O') {
          familyDescription = 'ADICIONAL';
        } else {
          familyDescription = 'OTRAS';
        }

        return {
          codigo: planCoverage.codigo,
          description: planCoverage.descripcionLarga,
          familyCode:
            planCoverage?.codigoFamilia ??
            (planCoverage.tipoDeCobertura === 'O'
              ? 'additional'
              : 'noFamilyCode'),
          familyDescription,
          key: planCoverage.codigo,
          riskCode: planCoverage.codigoRiesgo,
          sumInsured: `$ ${formattedPrice(
            `${matchedCoverage ? matchedCoverage.sumaAsegurada : 0}`
          )}`,
          typeCoverage: planCoverage?.tipoDeCobertura,
        };
      });

      const groupedData = coveragesListInfo.reduce<{
        [key: string]: Coverage[];
      }>((acc, item) => {
        let codeFamilyData: string;

        codeFamilyData = item.familyCode;

        if (!acc[codeFamilyData]) acc[codeFamilyData] = [];

        acc[codeFamilyData].push(item);
        return acc;
      }, {});

      const desiredOrder = ['noFamilyCode', 'additional'];
      const orderedGroupedData: { [key: string]: Coverage[] } = {};

      desiredOrder.forEach((key) => {
        if (groupedData[key]) orderedGroupedData[key] = groupedData[key];
      });

      Object.keys(groupedData)
        .filter((key) => !desiredOrder.includes(key))
        .forEach((key) => {
          orderedGroupedData[key] = groupedData[key];
        });

        setCoveragesListInfo([orderedGroupedData]);

      return;
    }

    setCoveragesListInfo([]);
  }, [allCoveragesPerPlan, consortiumComponent.cobertura, consortiumComponent.nroComponente, stepTwoForm]);

  useEffect(() => {
    setCompleteCoveragesListInfo();
  }, [setCompleteCoveragesListInfo]);

  const { renewalQuotationState } = useContext(QuotationInfoContext);
  const isRenewal = !!renewalQuotationState;

  return (
    <div className='formCoverages'>
      <CardAnt>
        <Form
          formProps={{
            form: propertyDataForm,
            name: `activity-form-${consortiumComponent.nroComponente}`,
          }}
        >
          <ContentSection level='h3' title='Datos del bien'>
            <RowSize>
              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'Domicilio:',
                    name: `propertyDataAddress${consortiumComponent.nroComponente}`,
                    rules: validateForm.generateRules({
                      label: 'domicilio',
                      rules: ['required'],
                    }),
                    initialValue: isRenewal ? (propertyDataAddressValueByTab ?? '') : '',
                  }}
                  inputProps={{ placeholder: 'Senillosa 342 1B', disabled: !!propertyDataAddressValueByTab && isRenewal, }}                
                  />
              </ColSize>

              <ColSize size={25}>
                <SelectForm
                  formProps={{
                    label: 'Provincia:',
                    name: `propertyDataProvince${consortiumComponent.nroComponente}`,
                  }}
                  selectProps={{
                    disabled: true,
                    loading: provinceList?.length === 0,
                    options: provinceList,
                    showSearch: true,
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <SelectForm
                  formProps={{
                    label: 'Localidad:',
                    name: `propertyDataLocality${consortiumComponent.nroComponente}`,
                  }}
                  selectProps={{
                    disabled: true,
                    options: localitiesPerProvince,
                  }}
                />
              </ColSize>
            </RowSize>
          </ContentSection>
        </Form>

        <ContentSection level='h3' title='Cobertura' hasCard={false}>
        {coveragesListInfo?.map((coverageGroup: any) => {
          const familyCodes = Object.keys(coverageGroup);

          return familyCodes.map((familyCode, index) => {
            const coverages = coverageGroup[familyCode];

            return (
              <React.Fragment key={`${familyCode}-${index}`}>
                <ColSize size={100}>
                  <Typography.Title
                    level={5}
                    style={{
                      margin: 0,
                      marginBottom: 10,
                      color: COLORS.secondary[100],
                    }}
                  >
                    {coverages[0]?.familyDescription}
                  </Typography.Title>
                </ColSize>
                <Table
                  bordered
                  className='person-table'
                  columns={MOCK_TABLE_COLUMNS_HEADER}
                  dataSource={coverages}
                  pagination={false}
                  scroll={{ x: 'auto' }}
                />
              </React.Fragment>
            );
          });
        })}
      </ContentSection>
      <TransferRightsForm consortiumComponent={consortiumComponent} />
      </CardAnt>
    </div>
  );
};
